/**
 * TypeScript types matching the CRM Documents GraphQL schema.
 * These are used for typing Apollo Client query results and variables.
 *
 * Source: libs/api/crm-documents/feature-documents/src/lib/documents.graphql
 */

// ============================================================================
// Enums
// ============================================================================

export enum DocumentType {
  RECHNUNG = 'RECHNUNG',
  GUTSCHRIFT = 'GUTSCHRIFT',
  ANGEBOT = 'ANGEBOT',
  AUFTRAG = 'AUFTRAG',
  LIEFERSCHEIN = 'LIEFERSCHEIN',
}

export enum SortField {
  DATE = 'DATE',
  DOCUMENT_NUMBER = 'DOCUMENT_NUMBER',
  NET_TOTAL = 'NET_TOTAL',
  COMMISSION = 'COMMISSION',
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum DocumentRelation {
  PREDECESSOR = 'PREDECESSOR',
  SUCCESSOR = 'SUCCESSOR',
}

export enum ArticleSortField {
  ARTICLE_NUMBER = 'ARTICLE_NUMBER',
  DESCRIPTION = 'DESCRIPTION',
  LAST_PURCHASE_DATE = 'LAST_PURCHASE_DATE',
  LAST_NET_UNIT_PRICE = 'LAST_NET_UNIT_PRICE',
  TOTAL_QUANTITY = 'TOTAL_QUANTITY',
  DOCUMENT_COUNT = 'DOCUMENT_COUNT',
}

export enum MaterialRequestStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum NetUnitPriceStatus {
  AVAILABLE = 'AVAILABLE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  LEGACY_MISSING = 'LEGACY_MISSING',
}

export enum LineType {
  ARTICLE = 'ARTICLE',
  TEXT = 'TEXT',
  SEPARATOR = 'SEPARATOR',
}

export enum NotificationFrequency {
  INSTANT = 'INSTANT',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  NONE = 'NONE',
}

// ============================================================================
// Core Types
// ============================================================================

export interface Document {
  id: string;
  type: DocumentType;
  documentNumber: string;
  customerNumber: string | null;
  customerName: string | null;
  customerName2: string | null;
  contact: string | null;
  yourReference: string | null;
  commission: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  documentDate: string;
  locationCode: string | null;
  shipmentWeek: string | null;
  systemId: string | null;
  jsonKey: string;
  pdfKey: string | null;
  netTotal: number | null;
  grossTotal: number | null;
  processedAt: string;
  lines: DocumentLine[];
  lineCount: number;
  netUnitPriceStatus: NetUnitPriceStatus;
  /** Lesestatus für den eingeloggten User. null = nicht aufgelöst (→ als gelesen behandeln). */
  isRead: boolean | null;
  /** true, wenn dieses Angebot bereits bestellt wurde (Button gesperrt). null = nicht aufgelöst. */
  isOrdered: boolean | null;
  /** Bestell-Zeitpunkt (ISO) oder null, wenn nicht bestellt. */
  orderedAt: string | null;
  searchContext?: DocumentSearchContext | null;
}

export interface DocumentLine {
  lineNumber: string;
  lineType: LineType;
  articleNumber: string | null;
  description: string | null;
  description2: string | null;
  /** Total quantity in base unit (e.g. 30 m²). */
  quantity: number | null;
  /** Number of packaging units (source field `Anz`). Null when absent. */
  packageQuantity: number | null;
  /** Quantity per packaging unit in base unit (source `MengeProPaket`). Null when absent. */
  quantityPerPackage: number | null;
  /** Pieces / VEs per outer pack (source `StkProPaket`). Used for three-tier
   *  pack hierarchies: packageQuantity × piecesPerPackage × quantityPerPackage = quantity. */
  piecesPerPackage: number | null;
  unit: string | null;
  unitPrice: number | null;
  netUnitPrice: number | null;
  discount: number | null;
  amount: number | null;
  isReturn: boolean;
  priceFactor: number;
  /** Hersteller, angezeigt unter der Artikelnummer. Null wenn unbekannt. */
  manufacturer?: string | null;
  deliveryNumber: string | null;
  orderNumber: string | null;
  document?: Document;
}

/** Eine Belegposition, die den Suchbegriff enthält. */
export interface MatchedLine {
  lineNumber: string;
  articleNumber: string | null;
  description: string;
  description2: string | null;
  quantity: number | null;
  unit: string | null;
  /**
   * Hersteller der Position — erklärt Markentreffer, bei denen die Marke im
   * angezeigten Text nicht vorkommt ("RHZ Dachrinne" für Rheinzink).
   */
  manufacturer?: string | null;
}

/**
 * Trefferbegründung eines Belegs. Nur gesetzt, wenn die Liste mit
 * `withPositions: true` und Suchbegriff abgefragt wurde UND mindestens eine
 * Position matcht. Bei Kopf-Treffern null.
 */
export interface DocumentSearchContext {
  lines: MatchedLine[];
  totalCount: number;
}

// ============================================================================
// Pagination (Relay Connection Pattern)
// ============================================================================

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

export interface DocumentEdge {
  node: Document;
  cursor: string;
}

export type SearchMatchKind = 'EXACT' | 'SIMILAR';

/** Von der Suchmaschine getroffenes Token — Grundlage für die
 *  Hervorhebung im Beleg (auch bei unscharfen Treffern). */
export interface SearchMatchToken {
  token: string;
  kind: SearchMatchKind;
}

export interface DocumentConnection {
  edges: DocumentEdge[];
  pageInfo: PageInfo;
  totalCount: number;
  /** null ohne Suchbegriff oder im Postgres-Fallback. */
  searchMatches?: SearchMatchToken[] | null;
}

export interface DocumentLineEdge {
  node: DocumentLine;
  cursor: string;
}

export interface DocumentLineConnection {
  edges: DocumentLineEdge[];
  pageInfo: PageInfo;
  totalCount: number;
}

// ============================================================================
// Statistics
// ============================================================================

export interface DocumentStats {
  totalDocuments: number;
  byType: DocumentTypeCount[];
  dateRange: DocumentDateRange;
}

export interface DocumentTypeCount {
  type: DocumentType;
  count: number;
}

export interface DocumentDateRange {
  earliest: string | null;
  latest: string | null;
}

export interface DocumentTypeInfo {
  id: number;
  name: string;
}

export interface DashboardStats {
  unreadOffers: number;
  unreadOrders: number;
  unreadInvoices: number;
  unreadDeliveryNotes: number;
}

/**
 * Rechnungssumme (netto): Rechnungen − Gutschriften, serverseitig aggregiert.
 * earliestDocDate = ältester Rechnungs-/Gutschrift-Beleg im Portalbestand
 * (unabhängig vom Datumsfilter) für den Hinweis „Belege ab …".
 */
export interface InvoiceTotals {
  netInvoiceTotal: number;
  netCreditTotal: number;
  netSum: number;
  invoiceCount: number;
  creditCount: number;
  earliestDocDate: string | null;
}

// ============================================================================
// Document Chain
// ============================================================================

export interface RelatedDocument {
  id: string;
  type: DocumentType;
  documentNumber: string;
  documentDate: string;
  relation: DocumentRelation;
  /** Brutto-Total in EUR. Null bei Belegen ohne Geldbetrag (z. B. Lieferschein). */
  grossTotal: number | null;
  /** Anzahl ARTICLE-Lines (Positionen). 0 bei Belegen ohne Artikelzeilen. */
  lineCount: number;
}

// ============================================================================
// Customer Articles
// ============================================================================

export interface CustomerArticle {
  articleNumber: string;
  description: string;
  description2: string | null;
  unit: string;
  imageUrl: string | null;
  /** Last invoice (VRG) net unit price (NettoPE; falls back to gross for legacy invoices). Null when never invoiced for this customer. */
  lastNetUnitPrice: number | null;
  /** Last invoice (VRG) document date. Null when the article was never invoiced. */
  lastPurchaseDate: string | null;
  /** Last invoice (VRG) document number. Null when the article was never invoiced. */
  lastDocumentNumber: string | null;
  /** Last invoice (VRG) document type — always RECHNUNG when set. Null when the article was never invoiced. */
  lastDocumentType: DocumentType | null;
  totalQuantity: number;
  documentCount: number;
  isFavorite: boolean;
  /**
   * Hersteller, angezeigt neben der Artikelnummer. Kommt aus dem
   * Navision-Artikelstamm — der Belegexport selbst kennt kein Herstellerfeld.
   * Fällt auf den Herstellercode zurück, wenn kein Klartextname bekannt ist.
   * Null, wenn für den Artikel kein Hersteller gepflegt ist.
   */
  manufacturer?: string | null;
}

export interface CustomerArticleEdge {
  node: CustomerArticle;
  cursor: string;
}

export interface CustomerArticleConnection {
  edges: CustomerArticleEdge[];
  pageInfo: PageInfo;
  totalCount: number;
  /** null ohne Suchbegriff, bei Belegnummern-Suche oder im Postgres-Fallback. */
  searchMatches?: SearchMatchToken[] | null;
}

// ============================================================================
// Favorites
// ============================================================================

export interface FavoriteItem {
  articleNumber: string;
  description: string;
  description2: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  labels: string[];
  pinned: boolean;
  imageUrl: string | null;
  /** Zeitpunkt des Favorisierens (ISO-String). Treibt die Recency-Sortierung. */
  addedAt: string | null;
  /** Letzter Netto-Einzelpreis (NettoPE, VRG; Fallback Einzelpreis). Null, wenn der Artikel nie berechnet wurde. */
  lastNetUnitPrice: number | null;
  /** Datum der letzten Rechnung (ISO-String). Null, wenn nie berechnet. */
  lastPurchaseDate: string | null;
  /** Belegnummer der letzten Rechnung. Null, wenn nie berechnet. */
  lastDocumentNumber: string | null;
  /** Belegtyp der letzten Rechnung — immer RECHNUNG, wenn gesetzt. Null, wenn nie berechnet. */
  lastDocumentType: DocumentType | null;
}

export interface FavoriteLabel {
  name: string;
  /** Palette-ID ("blue" | "green" | "violet" | "rose" | "amber" | "teal" | "slate"). Null = aus dem Namen abgeleitet. */
  color: string | null;
  /** Sortier-Reihenfolge der Filter-Chips. */
  sortOrder: number;
}

// ============================================================================
// Articles
// ============================================================================

export interface Article {
  articleNumber: string;
  name: string;
  description2: string | null;
  unit: string;
  basePrice: number;
  imageUrl: string | null;
  ean: string | null;
}

// ============================================================================
// Material Templates
// ============================================================================

export interface MaterialTemplate {
  id: string;
  name: string;
  itemCount: number;
  lastUsed: string | null;
  items: MaterialTemplateItem[];
  labels: string[];
  /**
   * Optionale Akzentfarbe für die Projekt-Liste ("orange" | "blue" | "green" |
   * "violet" | "amber"). Null = neutral.
   */
  accentColor: string | null;
}

export interface MaterialTemplateItem {
  articleNumber: string;
  quantity: number;
  /** Artikelbezeichnung (desc1), serverseitig aus den Kunden-Belegen aufgeloest. Null, wenn unbekannt. */
  description?: string | null;
  /** Zweite Bezeichnungszeile (desc2), serverseitig aufgeloest. Null, wenn unbekannt. */
  description2?: string | null;
  /** Einheit (z.B. Stück, m²), serverseitig aufgeloest. Null, wenn unbekannt. */
  unit?: string | null;
  /**
   * Menge pro VE (MengeProPaket), serverseitig aus den Kunden-Belegen aggregiert.
   * Treibt Schrittweite und Sub-Text-Aufschlüsselung im Mengen-Input.
   * Null, wenn der Artikel keine VE hat oder noch nie bestellt wurde.
   */
  quantityPerPackage?: number | null;
  /**
   * Stk/VEs pro Außenpack (StkProPaket). Drei-Stufen-Hierarchie zusammen mit
   * quantityPerPackage. Null, wenn nicht gesetzt.
   */
  piecesPerPackage?: number | null;
  /**
   * Optionale Pro-Position-Notiz. Wird beim "Zur Anfrage"-Flow 1:1 als
   * MaterialRequestItem.note an die Materialanfrage übergeben.
   */
  note?: string | null;
}

export interface MaterialTemplateLabel {
  name: string;
  /**
   * Persistierte Label-Farbe als Palette-ID ("blue" | "green" | "violet" | "rose" |
   * "amber" | "teal" | "slate"). Null = aus dem Namen abgeleitete Fallback-Farbe.
   */
  color: string | null;
  /** Sortier-Reihenfolge der Filter-Chips (per Drag im „Labels verwalten"-Sheet). */
  sortOrder: number;
}

// ============================================================================
// Material Requests
// ============================================================================

export interface MaterialRequest {
  id: string;
  name: string | null;
  status: MaterialRequestStatus;
  createdAt: string;
  updatedAt: string;
  commission: string | null;
  desiredDeliveryDate: string | null;
  desiredDeliveryWeek: string | null;
  note: string | null;
  /** NAV-Quote-Nummer (z.B. "ANPA0000042"). Null solange DRAFT oder bei Import-Fehler. */
  navDocumentNo: string | null;
  /** Zeitpunkt der erfolgreichen NAV-Übertragung (ISO-DateTime). Null wenn nicht übertragen. */
  navImportedAt: string | null;
  items: MaterialRequestItem[];
  netTotal: number;
}

export interface MaterialRequestItem {
  id: string;
  articleNumber: string;
  description: string;
  description2: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  note: string | null;
  imageUrl: string | null;
  quantityPerPackage: number | null;
  piecesPerPackage: number | null;
  priceFactor: number | null;
}

export interface MaterialRequestEdge {
  node: MaterialRequest;
  cursor: string;
}

export interface MaterialRequestConnection {
  edges: MaterialRequestEdge[];
  pageInfo: PageInfo;
  totalCount: number;
}

// ============================================================================
// Sales Representative
// ============================================================================

export interface SalesRepresentative {
  name: string;
  phone: string;
  email: string;
  photoUrl: string | null;
  role: string;
  /** NAV-Heimatfiliale des Verkäufers (KAARST | DDORF | ORDERSHOP | …); null wenn nicht gesetzt. */
  locationCode: string | null;
}

// ============================================================================
// Input Types (for mutation variables)
// ============================================================================

export interface DocumentFilterInput {
  types?: DocumentType[];
  searchText?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  locationCode?: string;
  commission?: string;
  yourReference?: string;
}

export interface ArticleHistoryInput {
  articleNumber: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface MaterialTemplateItemInput {
  articleNumber: string;
  quantity: number;
  /** Optionale Pro-Position-Notiz; null/undefined = keine Notiz. */
  note?: string | null;
}

export interface MaterialRequestItemInput {
  articleNumber: string;
  quantity: number;
  note?: string;
}

// ============================================================================
// Notifications
// ============================================================================

export interface NotificationPrefsData {
  frequency: NotificationFrequency;
  docTypes: DocumentType[];
  onboardingDone: boolean;
  disabledForAdmin: boolean;
}
